/**
 * Persistent Storage Utility für iOS Safari Kompatibilität
 * Nutzt IndexedDB (primär) + localStorage (fallback) + Cookies
 */

const DB_NAME = 'hayat_storage';
const DB_VERSION = 1;
const STORE_NAME = 'auth';

class PersistentStorage {
  constructor() {
    this.db = null;
    this.initDB();
  }

  // IndexedDB initialisieren
  async initDB() {
    if (!window.indexedDB) {
      console.warn('⚠️ IndexedDB nicht verfügbar, nutze localStorage');
      return;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('❌ IndexedDB Fehler:', request.error);
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        console.log('✅ IndexedDB initialisiert');
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME);
        }
      };
    });
  }

  // Wert speichern (IndexedDB + localStorage + Cookie)
  async setItem(key, value) {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);

    // 1. In localStorage speichern (schnell)
    try {
      localStorage.setItem(key, stringValue);
    } catch (e) {
      console.warn('⚠️ localStorage.setItem fehlgeschlagen:', e);
    }

    // 2. In IndexedDB speichern (persistent auf iOS)
    try {
      await this.setItemIndexedDB(key, stringValue);
    } catch (e) {
      console.warn('⚠️ IndexedDB.setItem fehlgeschlagen:', e);
    }

    // 3. Als Cookie speichern (zusätzliche Absicherung für iOS)
    try {
      this.setCookie(key, stringValue, 365); // 1 Jahr
    } catch (e) {
      console.warn('⚠️ Cookie.setItem fehlgeschlagen:', e);
    }

    return true;
  }

  // Wert abrufen (versuche alle Quellen)
  async getItem(key) {
    // 1. Versuche localStorage (schnellster Zugriff)
    try {
      const localValue = localStorage.getItem(key);
      if (localValue) {
        console.log(`✅ ${key} aus localStorage geladen`);
        return localValue;
      }
    } catch (e) {
      console.warn('⚠️ localStorage.getItem fehlgeschlagen:', e);
    }

    // 2. Versuche IndexedDB
    try {
      const idbValue = await this.getItemIndexedDB(key);
      if (idbValue) {
        console.log(`✅ ${key} aus IndexedDB geladen`);
        // Sync zurück zu localStorage
        try {
          localStorage.setItem(key, idbValue);
        } catch (e) {}
        return idbValue;
      }
    } catch (e) {
      console.warn('⚠️ IndexedDB.getItem fehlgeschlagen:', e);
    }

    // 3. Versuche Cookie als letzter Fallback
    try {
      const cookieValue = this.getCookie(key);
      if (cookieValue) {
        console.log(`✅ ${key} aus Cookie geladen`);
        // Sync zurück zu anderen Storages
        try {
          localStorage.setItem(key, cookieValue);
          await this.setItemIndexedDB(key, cookieValue);
        } catch (e) {}
        return cookieValue;
      }
    } catch (e) {
      console.warn('⚠️ Cookie.getItem fehlgeschlagen:', e);
    }

    console.log(`⚠️ ${key} in keinem Storage gefunden`);
    return null;
  }

  // Wert löschen (aus allen Storages)
  async removeItem(key) {
    // localStorage
    try {
      localStorage.removeItem(key);
    } catch (e) {}

    // IndexedDB
    try {
      await this.removeItemIndexedDB(key);
    } catch (e) {}

    // Cookie
    try {
      this.deleteCookie(key);
    } catch (e) {}

    return true;
  }

  // === IndexedDB Helpers ===
  async setItemIndexedDB(key, value) {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(value, key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  async getItemIndexedDB(key) {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) return null;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(key);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removeItemIndexedDB(key) {
    if (!this.db) {
      await this.initDB();
    }
    if (!this.db) return;

    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(key);

      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    });
  }

  // === Cookie Helpers ===
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Strict${secure}`;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }
    return null;
  }

  deleteCookie(name) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  }
}

// Singleton Instance
const storage = new PersistentStorage();

export default storage;
